/**
 * YMM (Year/Make/Model) utility functions for WKS Product Entry
 * 
 * These functions handle YMM restriction formatting and validation for the frontend.
 */

export interface YMMRestriction {
  make: string;
  model: string;
  yearFrom: number;
  yearTo: number;
}

/**
 * Minimum valid year for YMM restrictions
 */
export const YMM_YEAR_MIN = 1950;

/**
 * Maximum valid year for YMM restrictions
 */
export const YMM_YEAR_MAX = 2030;

/**
 * Serialize a YMM restriction to the comma-separated format expected by YMM Search plugin
 * 
 * Format: "make, model, yearFrom, yearTo"
 * 
 * @param restriction - The YMM restriction object
 * @returns The serialized YMM restriction string
 * 
 * **Feature: woocommerce-product-entry, Property 4: YMM Serialization Format**
 * **Validates: Requirements 2.3**
 */
export function serializeYMMRestriction(restriction: YMMRestriction): string {
  const { make, model, yearFrom, yearTo } = restriction;
  return `${make}, ${model}, ${yearFrom}, ${yearTo}`;
}

/**
 * Parse a serialized YMM restriction string back to an object
 * 
 * @param serialized - The serialized YMM restriction string
 * @returns The parsed YMM restriction object or null if invalid
 */
export function parseYMMRestriction(serialized: string): YMMRestriction | null {
  const parts = serialized.split(',').map(part => part.trim());
  
  if (parts.length !== 4) {
    return null;
  }
  
  const [make, model, yearFromStr, yearToStr] = parts;
  const yearFrom = parseInt(yearFromStr, 10);
  const yearTo = parseInt(yearToStr, 10);
  
  if (isNaN(yearFrom) || isNaN(yearTo)) {
    return null;
  }
  
  return { make, model, yearFrom, yearTo };
}

/**
 * Validate a year value for YMM restrictions
 * 
 * @param year - The year to validate
 * @returns True if the year is within the valid range (1950-2030) or is 0 (unset)
 */
export function isValidYMMYear(year: number): boolean {
  if (year === 0) {
    return true; // 0 means unset/any year
  }
  return Number.isInteger(year) && year >= YMM_YEAR_MIN && year <= YMM_YEAR_MAX;
}

/**
 * Validate a complete YMM restriction
 * 
 * @param restriction - The YMM restriction to validate
 * @returns True if the restriction is valid
 */
export function isValidYMMRestriction(restriction: YMMRestriction): boolean {
  const { make, model, yearFrom, yearTo } = restriction;
  
  // At least make or model should be provided
  if (!make && !model && yearFrom === 0 && yearTo === 0) {
    return false;
  }
  
  // Validate year range
  if (!isValidYMMYear(yearFrom) || !isValidYMMYear(yearTo)) {
    return false;
  }
  
  // If both years are set, yearFrom should be <= yearTo
  if (yearFrom > 0 && yearTo > 0 && yearFrom > yearTo) {
    return false;
  }
  
  return true;
}

/**
 * Create an empty YMM restriction
 * 
 * @returns A new empty YMM restriction object
 */
export function createEmptyYMMRestriction(): YMMRestriction {
  return {
    make: '',
    model: '',
    yearFrom: 0,
    yearTo: 0,
  };
}

/**
 * Add a YMM restriction to a list
 * 
 * @param list - The current list of restrictions
 * @param restriction - The restriction to add
 * @returns A new list with the restriction added
 */
export function addYMMRestriction(
  list: YMMRestriction[],
  restriction: YMMRestriction
): YMMRestriction[] {
  return [...list, restriction];
}

/**
 * Remove a YMM restriction from a list by index
 * 
 * @param list - The current list of restrictions
 * @param index - The index of the restriction to remove
 * @returns A new list with the restriction removed
 */
export function removeYMMRestriction(
  list: YMMRestriction[],
  index: number
): YMMRestriction[] {
  if (index < 0 || index >= list.length) {
    return list;
  }
  return list.filter((_, i) => i !== index);
}
